import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Send, Mail, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', order_number: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    const orderParam = searchParams.get('order');
    if (orderParam) {
      setForm(prev => ({ ...prev, order_number: orderParam }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (user?.email) {
      setForm(prev => ({
        ...prev,
        email: prev.email || user.email,
        first_name: prev.first_name || user.first_name || user.name?.split(' ')[0] || '',
        last_name: prev.last_name || user.last_name || user.name?.split(' ').slice(1).join(' ') || '',
      }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      // Prepend order ref into the message body so the admin sees it inline
      if (form.order_number) {
        payload.message = `Order #${form.order_number}\n\n${form.message}`;
      }
      delete payload.order_number;
      await axios.post(`${API}/contact`, payload);
      setSent(true);
      toast.success('Message sent successfully!');
    } catch { toast.error('Failed to send message'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="contact-page">
      <SEO title="Contact Us" description="Get in touch with Wilvada Peptide Solutions — order support, COA requests, fulfillment questions, and bulk-order inquiries." />
      <div className="text-center mb-10">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[hsl(210,45%,40%)] mb-2 font-body">Contact</p>
        <h1 className="text-4xl sm:text-5xl tracking-tight font-light text-foreground font-heading mb-4">Get in Touch</h1>
        <p className="text-base text-muted-foreground font-body">You can reach us directly at <a href="mailto:support@wilvada.com" className="text-[hsl(210,45%,40%)] font-medium hover:underline">support@wilvada.com</a> or send us a message using the form below.</p>
      </div>

      {sent ? (
        <Card data-testid="contact-success">
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-heading font-semibold mb-2">Message Sent!</h2>
            <p className="text-muted-foreground font-body mb-4">We'll get back to you as soon as possible.</p>
            <Button variant="outline" onClick={() => { setSent(false); setForm({ first_name: '', last_name: '', email: '', order_number: '', message: '' }); }}>Send Another Message</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Mail className="h-5 w-5" /> Send us a Message</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1"><Label className="font-body text-sm">First Name *</Label><Input value={form.first_name} onChange={e => update('first_name', e.target.value)} required data-testid="contact-first-name" /></div>
                <div className="space-y-1"><Label className="font-body text-sm">Last Name *</Label><Input value={form.last_name} onChange={e => update('last_name', e.target.value)} required data-testid="contact-last-name" /></div>
              </div>
              <div className="space-y-1"><Label className="font-body text-sm">Email *</Label><Input type="email" value={form.email} onChange={e => update('email', e.target.value)} required data-testid="contact-email" /></div>
              <div className="space-y-1"><Label className="font-body text-sm">Order Number (Optional)</Label><Input type="text" placeholder="e.g. 320028" value={form.order_number} onChange={e => update('order_number', e.target.value)} data-testid="contact-order-number" /></div>
              <div className="space-y-1"><Label className="font-body text-sm">Message *</Label><Textarea rows={5} value={form.message} onChange={e => update('message', e.target.value)} required data-testid="contact-message" /></div>
              <Button type="submit" className="w-full bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white gap-2" disabled={loading} data-testid="contact-submit">
                {loading ? 'Sending...' : <><Send className="h-4 w-4" /> Send Message</>}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
